import React, { useEffect, useState } from "react";
import { highlight } from "./highlight";
import { fileUrl, readFile } from "../../utils/project";

// Files already fetched and highlighted, so switching tabs is instant.
const cache = new Map();

const load = (path) => {
  if (!cache.has(path)) {
    cache.set(
      path,
      readFile(path)
        .then(async (text) => ({ text, html: await highlight(text, path.split("/").pop()) }))
        .catch((error) => {
          cache.delete(path);
          throw error;
        }),
    );
  }
  return cache.get(path);
};

// A file's text with line numbers down the gutter, highlighted once Prism has loaded.
export const CodeView = ({ path }) => {
  const [file, setFile] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFile(null);
    setFailed(false);
    load(path).then(
      (f) => live && setFile(f),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [path]);

  if (failed) return <p className="zd-message">This file couldn't be loaded.</p>;
  if (!file) return <div className="zd-code" aria-busy="true" />;
  const lines = file.text.split("\n").length - (file.text.endsWith("\n") ? 1 : 0);
  return (
    <div className="zd-code">
      <div className="zd-gutter" aria-hidden="true">
        {Array.from({ length: Math.max(lines, 1) }, (_, i) => (
          <span key={i}>{i + 1}</span>
        ))}
      </div>
      {file.html ? (
        <pre className="zd-text">
          <code dangerouslySetInnerHTML={{ __html: file.html }} />
        </pre>
      ) : (
        <pre className="zd-text">
          <code>{file.text}</code>
        </pre>
      )}
    </div>
  );
};

// An image at its own size times `zoom`, or fitted to the pane.
export const ImageView = ({ path, zoom, fit }) => (
  <div className={`zd-image ${fit ? "is-fit" : ""}`}>
    <img
      src={fileUrl(path)}
      alt={path.split("/").pop()}
      draggable={false}
      style={fit ? undefined : { width: "auto", zoom }}
    />
  </div>
);

export const Message = ({ children }) => <p className="zd-message">{children}</p>;
